import React, { useEffect } from 'react';
import AACChromeAPI, { ChromeEvent } from '@amzn/aac-chrome-api';
import { connect } from 'react-redux';
import { RootState } from 'src/app/store';
import {
  Flag,
  selectIsMobileWeb,
  selectViewType,
  VIEW_TYPE,
} from 'src/app/slice';
import { selectHeaderState } from 'src/features/header/store/slice';
import { LinkProps } from '@amzn/aac-chrome-internal-api';
import { IconButton, MobileMode, MobileThemeProvider } from '@amzn/storm-ui';
import { close } from '@amzn/storm-ui-icons';
import { PageTitleTag } from 'src/commons/styles';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { Actions } from '../PageHeader/Actions/Actions';
import { selectRenderTagInPageName } from 'src/features/nav/store/slice';
import {
  FocusHeaderSection,
  FocusHeaderSpacer,
  FocusHeaderWrapper,
  HeadingArea,
  PageTitle,
} from './styles';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { formatFlagType } from 'src/commons/components/LaunchFlag/LaunchFlagUtils';
import { usePageHeaderBundle } from 'src/providers/PantherLocalizationProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

export const FocusHeader: React.VFC<{
  helpInfo?: LinkProps[];
  isMobileDevice?: boolean;
  pageTitleTag?: Flag | null;
  isMobileView?: boolean;
}> = ({ helpInfo, isMobileDevice, pageTitleTag, isMobileView }) => {
  useEffect(() => {
    didElementLoad('focusNav');
  }, []);
  const bundle = usePageHeaderBundle();
  const chromeConfig = useChromeConfigContext();
  const pageName = chromeConfig.pageHeader?.pageName;
  const objectInfo = chromeConfig.pageHeader?.objectInfo;
  const actions = objectInfo?.actions;
  const errorMessage = objectInfo?.actionErrorMessage;

  const handleCloseButtonClick = (event) => {
    if (typeof document !== 'undefined') {
      const eventTarget = AACChromeAPI.getCommonEventTarget();
      const chromeEvent = new ChromeEvent('ExitFocusView');
      chromeEvent.detail = {
        originEvent: event,
      };
      eventTarget.dispatchEvent(chromeEvent);
    }
  };

  if (!isMobileDevice) {
    return (
      <FocusHeaderWrapper id="focusNav">
        <FocusHeaderSection>
          <IconButton
            onClick={handleCloseButtonClick}
            data-testid="aac-exit-focus-layout"
            data-ccx-e2e-id="aac-exit-focus-layout"
            aria-label={bundle.getMessage(
              'page-header-popover-close-button-aria-label',
            )}
            label={bundle.getMessage(
              'page-header-popover-close-button-aria-label',
            )}
            variant="transparent"
            icon={close}
            {...getTaktAttributes(
              'aac-exit-focus-mode',
              chromeComponents.HEADER,
            )}
          />
          <HeadingArea>
            {pageName && (
              <PageTitle>
                {pageName}
                {pageTitleTag &&
                  (() => {
                    const flagType = formatFlagType(pageTitleTag.type);

                    return (
                      <PageTitleTag
                        variant={
                          flagType !== 'beta_ai_with_icon'
                            ? flagType
                            : undefined
                        }
                        label={pageTitleTag.label}
                        description={
                          pageTitleTag.description ?? pageTitleTag.label
                        }
                      />
                    );
                  })}
              </PageTitle>
            )}
          </HeadingArea>
        </FocusHeaderSection>
        <FocusHeaderSection>
          <Actions
            actions={actions?.list}
            errorMessage={errorMessage}
            helpInfo={helpInfo}
            isFocusPage
          />
        </FocusHeaderSection>
      </FocusHeaderWrapper>
    );
  }

  return (
    <FocusHeaderWrapper id="focusNav">
      <MobileThemeProvider mobileMode={MobileMode.Enable}>
        <FocusHeaderSection>
          <IconButton
            onClick={handleCloseButtonClick}
            data-ccx-e2e-id="aac-exit-focus-layout"
            aria-label={bundle.getMessage(
              'page-header-popover-close-button-aria-label',
            )}
            label={bundle.getMessage(
              'page-header-popover-close-button-aria-label',
            )}
            variant="transparent"
            icon={close}
            {...getTaktAttributes(
              'aac-exit-focus-mode',
              chromeComponents.HEADER,
            )}
          />
          <HeadingArea>
            {pageName && <PageTitle>{pageName}</PageTitle>}
          </HeadingArea>
        </FocusHeaderSection>
        <FocusHeaderSpacer data-ccx-e2e-id="aac-focus-header-spacer" />
        <FocusHeaderSection>
          <Actions
            isMobileView={isMobileView}
            actions={[]}
            helpInfo={helpInfo}
            isFocusPage
          />
        </FocusHeaderSection>
      </MobileThemeProvider>
    </FocusHeaderWrapper>
  );
};

const mapStateToProps = (state: RootState) => {
  const viewType = selectViewType(state);
  const isMobileView = viewType === VIEW_TYPE.MOBILE;
  return {
    helpInfo: selectHeaderState(state).helpInfo,
    isMobileDevice: selectIsMobileWeb(state),
    pageTitleTag: selectRenderTagInPageName(state),
    isMobileView: isMobileView,
  };
};
export const ConnectedFocusHeader = connect(mapStateToProps)(FocusHeader);
